import React, { useState, useEffect } from "react";
import { Calendar } from "lucide-react";

// Format date to a human-friendly format without timezone manipulation
const formatDateHeader = (dateString) => {
  // Parse the date string directly without creating a new Date object
  const [year, month, day] = dateString.split("-");

  // Create a date object using the individual components to avoid timezone shifts
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

  return date.toLocaleDateString("en-US", {
    weekday: "short", // e.g. "Mon"
    month: "short", // e.g. "Sep"
    day: "numeric", // e.g. "4"
  });
};

// Helper for color classes based on available courts
const getCourtAvailabilityColor = (courts) => {
  if (courts === 0) return "bg-red-100 text-red-800";
  if (courts < 3) return "bg-yellow-100 text-yellow-800";
  if (courts < 6) return "bg-green-100 text-green-800";
  return "bg-green-200 text-green-900";
};

const CourtAvailabilityMatrix = () => {
  const [courtData, setCourtData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCourtData = async () => {
      try {
        const response = await fetch("https://connorvps.com/ggp-courts/");
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        setCourtData(data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    fetchCourtData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-2xl font-bold">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen text-red-600">
        Error: {error}
      </div>
    );
  }

  if (!courtData) return null;

  // Extract unique dates & times
  const uniqueDates = Array.from(
    new Set(courtData.data.map((item) => item.Date))
  ).sort();

  const uniqueTimes = Array.from(
    new Set(courtData.data.map((item) => item.Time))
  ).sort((a, b) => {
    // Convert time to 24-hour format for proper sorting
    const convertTo24Hour = (timeStr) => {
      const [time, modifier] = timeStr.split(" ");
      let [hours, minutes] = time.split(":");
      hours = parseInt(hours, 10);

      if (modifier === "PM" && hours !== 12) {
        hours += 12;
      }
      if (modifier === "AM" && hours === 12) {
        hours = 0;
      }

      return `${hours.toString().padStart(2, "0")}:${minutes}`;
    };

    return convertTo24Hour(a).localeCompare(convertTo24Hour(b));
  });

  // Build a lookup: dayTimeMap[date][time] = available courts
  const dayTimeMap = {};
  courtData.data.forEach((item) => {
    const { Date, Time, AvailableCourts } = item;
    if (!dayTimeMap[Date]) {
      dayTimeMap[Date] = {};
    }
    dayTimeMap[Date][Time] = AvailableCourts;
  });

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Title */}
      <div className="flex items-center mb-4">
        <Calendar className="text-gray-600 mr-2" />
        <h2 className="text-2xl font-bold">
          Number of Courts Available at Lisa & Douglas Goldman Tennis Center
        </h2>
      </div>

      {/* Matrix Table */}
      <div className="overflow-x-auto bg-white shadow rounded-lg">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-50">
              {/* Left corner cell */}
              <th className="p-2 text-left border-b border-gray-200">Time</th>
              {/* One column per unique date */}
              {uniqueDates.map((date) => (
                <th
                  key={date}
                  className="p-2 text-center border-b border-l border-gray-200"
                >
                  {formatDateHeader(date)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* One row per time */}
            {uniqueTimes.map((time) => (
              <tr
                key={time}
                className="border-b last:border-b-0 border-gray-200"
              >
                {/* Left column = the time */}
                <td className="p-2 font-medium whitespace-nowrap">{time}</td>

                {/* Each subsequent cell = available courts for that day/time */}
                {uniqueDates.map((date) => {
                  const courts = dayTimeMap[date]?.[time] ?? 0; // or "—" if not found
                  return (
                    <td
                      key={date}
                      className={`p-2 text-center border-l border-gray-200 font-bold ${getCourtAvailabilityColor(
                        courts
                      )}`}
                    >
                      {courts}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Last Updated */}
      <div className="text-center text-gray-500 mt-4">
        Last Updated: {new Date(courtData.lastUpdated).toLocaleString()}
      </div>
    </div>
  );
};

export default CourtAvailabilityMatrix;
